import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Caldea Brand Colors (extracted from www.caldea.com)
const CALDEA_COLORS = {
  primary: '#1DCAFF',      // Caldea Cyan (from website)
  primaryLight: '#5DD8FF',
  primaryDark: '#00A9CE',
  secondary: '#36454F',    // Caldea Charcoal
  accent: '#E8F7FA',       // Light Cyan tint
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6'
};

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('caldea-theme-mode');
    return saved || 'light';
  });

  useEffect(() => {
    localStorage.setItem('caldea-theme-mode', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: CALDEA_COLORS.primary,
            light: CALDEA_COLORS.primaryLight,
            dark: CALDEA_COLORS.primaryDark,
          },
          secondary: {
            main: CALDEA_COLORS.secondary,
          },
          success: {
            main: CALDEA_COLORS.success,
          },
          warning: {
            main: CALDEA_COLORS.warning,
          },
          error: {
            main: CALDEA_COLORS.error,
          },
          info: {
            main: CALDEA_COLORS.info,
          },
          ...(mode === 'light'
            ? {
                // Light mode colors
                background: {
                  default: '#f8fafc',
                  paper: '#ffffff',
                  sidebar: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
                },
                text: {
                  primary: '#0f172a',
                  secondary: '#64748b',
                },
                caldea: {
                  cardBg: '#ffffff',
                  cardBorder: 'rgba(0,0,0,0.06)',
                  cardShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  cardHoverShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  appBarBg: 'rgba(255,255,255,0.9)',
                  appBarBorder: 'rgba(0,0,0,0.06)',
                  inputBg: '#f1f5f9',
                  tableHeaderBg: '#f8fafc',
                  highlightBg: 'rgba(29,202,255,0.08)',
                  chipBg: 'rgba(29,202,255,0.1)',
                }
              }
            : {
                // Dark mode colors
                background: {
                  default: '#0f172a',
                  paper: '#1e293b',
                  sidebar: 'linear-gradient(180deg, #0a0f1a 0%, #111827 100%)',
                },
                text: {
                  primary: '#f1f5f9',
                  secondary: '#94a3b8',
                },
                caldea: {
                  cardBg: '#1e293b',
                  cardBorder: 'rgba(255,255,255,0.08)',
                  cardShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  cardHoverShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  appBarBg: 'rgba(30,41,59,0.9)',
                  appBarBorder: 'rgba(255,255,255,0.06)',
                  inputBg: '#0f172a',
                  tableHeaderBg: '#0f172a',
                  highlightBg: 'rgba(29,202,255,0.15)',
                  chipBg: 'rgba(29,202,255,0.2)',
                }
              }),
        },
        typography: {
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          h1: { fontWeight: 700 },
          h2: { fontWeight: 700 },
          h3: { fontWeight: 600 },
          h4: { fontWeight: 600 },
          h5: { fontWeight: 600 },
          h6: { fontWeight: 600 },
        },
        shape: {
          borderRadius: 8,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 8,
              },
              containedPrimary: {
                background: `linear-gradient(135deg, ${CALDEA_COLORS.primary} 0%, ${CALDEA_COLORS.primaryDark} 100%)`,
                boxShadow: `0 4px 14px rgba(29,202,255,0.35)`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${CALDEA_COLORS.primaryLight} 0%, ${CALDEA_COLORS.primary} 100%)`,
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 12,
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                fontWeight: 500,
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                },
              },
            },
          },
        },
      }),
    [mode]
  );

  const value = {
    mode,
    toggleTheme,
    isDark: mode === 'dark',
    isLight: mode === 'light',
    colors: CALDEA_COLORS,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
